
import React, { useState } from 'react';
import { Language } from '../types';
import { translations } from '../translations';

interface HeaderProps {
  lang: Language;
  setLang: (lang: Language) => void;
}

const Header: React.FC<HeaderProps> = ({ lang, setLang }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const t = translations[lang].nav;

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 apple-blur border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        
        {/* Left Side: Logo */}
        <div className="flex items-center">
          <div 
            className="flex items-center text-2xl md:text-3xl font-bold tracking-tight cursor-pointer" 
            onClick={() => { window.scrollTo({top: 0, behavior: 'smooth'}); closeMenu(); }}
          >
            <span className="text-white">Axiomity</span>
            <span className="text-[#0ea5e9]">Lab</span>
          </div>
        </div>

        {/* Right Side: Navigation Links & Language */}
        <div className="flex items-center gap-4 md:gap-8">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8 text-sm font-semibold uppercase tracking-widest text-gray-400">
            <a href="#about" className="hover:text-white transition-colors">{t.advantages}</a>
            <a href="#performance" className="hover:text-white transition-colors">{t.results}</a>
            <a href="#pricing" className="hover:text-white transition-colors">{t.licenses}</a>
            <a href="#faq" className="hover:text-white transition-colors">{t.faqs}</a>
            <a href="#contact" className="hover:text-white transition-colors">{t.contact}</a>
          </div>

          {/* Language Toggle */}
          <button 
            onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
            className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all text-[10px] md:text-[11px] font-black uppercase tracking-[0.2em] group"
          >
            <span className={lang === 'es' ? 'text-sky-400' : 'text-gray-500 group-hover:text-gray-300'}>ES</span>
            <span className="text-gray-700">|</span>
            <span className={lang === 'en' ? 'text-sky-400' : 'text-gray-500 group-hover:text-gray-300'}>EN</span>
          </button>

          {/* Mobile Menu Button */}
          <button 
            onClick={toggleMenu}
            className="lg:hidden p-2 text-gray-400 hover:text-white transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`lg:hidden fixed inset-x-0 top-20 bg-black/95 backdrop-blur-2xl border-b border-white/10 transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-screen opacity-100 py-8' : 'max-h-0 opacity-0 py-0 pointer-events-none'}`}>
        <div className="flex flex-col items-center space-y-6 text-sm font-bold uppercase tracking-[0.3em]">
          <a href="#about" onClick={closeMenu} className="text-gray-400 hover:text-sky-400 transition-colors py-2">{t.advantages}</a>
          <a href="#performance" onClick={closeMenu} className="text-gray-400 hover:text-sky-400 transition-colors py-2">{t.results}</a>
          <a href="#pricing" onClick={closeMenu} className="text-gray-400 hover:text-sky-400 transition-colors py-2">{t.licenses}</a>
          <a href="#faq" onClick={closeMenu} className="text-gray-400 hover:text-sky-400 transition-colors py-2">{t.faqs}</a>
          <a href="#contact" onClick={closeMenu} className="text-gray-400 hover:text-sky-400 transition-colors py-2">{t.contact}</a>
        </div>
      </div>
    </nav>
  );
};

export default Header;
